<template>
  <AccountWrapper class="content">
    <template #title>{{ t('general.titles.account-user-info') }}</template>

    <form
      class="form mb-10"
      @submit.prevent="form.formWasChanged && submit(form)"
    >
      <AccountFormInputs :fields="form.fields" />

      <div class="input-block input-block--submit">
        <Button :disabled="!form.formWasChanged" type="submit">
          {{ t('account.form.save') }}
        </Button>
      </div>
    </form>

    <h4 class="mb-6 text-lg font-semibold">
      {{ t('account.changePassword') }}
    </h4>

    <form
      class="form"
      @submit.prevent="passwordForm.formWasChanged && submit(passwordForm)"
    >
      <AccountFormInputs :fields="passwordForm.fields" />

      <div class="input-block input-block--submit">
        <Button :disabled="!passwordForm.formWasChanged" type="submit">
          {{ t('account.form.save') }}
        </Button>
      </div>
    </form>
  </AccountWrapper>
</template>

<script setup lang="ts">
import { toast, ToastOptions } from 'vue3-toastify'

const { user, fetchProfile } = useAccount()
const { getAccessToken } = useAuthTokens()

const { t } = useI18n()

const userId = computed(() => user.value?.data?.id || '')
const customerId = computed(() => user.value?.data?.customer?.data?.id || '')

const form = reactive({
  url: `/v1/store/customers/${customerId.value}`,
  formWasChanged: false,
  fields: {
    first_name: {
      value: user.value?.data?.first_name,
      required: true,
      label: t('account.form.firstName'),
      error: false,
      type: 'text',
    },
    last_name: {
      value: user.value?.data?.last_name,
      required: true,
      label: t('account.form.lastName'),
      error: false,
      type: 'text',
    },
    email: {
      value: user.value?.data?.email,
      required: true,
      label: t('account.form.email'),
      error: false,
      type: 'email',
    },
    company_name: {
      value: user.value?.data?.customer?.data?.company_name || '',
      required: false,
      label: t('account.form.companyName'),
      error: false,
      type: 'text',
    },
  },
})

const passwordForm = reactive({
  url: `/v1/users/${userId.value}/password`,
  formWasChanged: false,
  fields: {
    password: {
      value: '',
      required: true,
      label: t('account.form.password'),
      error: false,
      type: 'password',
    },
    password_confirmation: {
      value: '',
      required: true,
      label: t('account.form.passwordConfirmation'),
      error: false,
      type: 'password',
    },
  },
})

watch(form.fields, () => {
  form.formWasChanged = true
})

watch(passwordForm.fields, () => {
  passwordForm.formWasChanged = true
})

const validateForm = (form: any): boolean => {
  let isValid = true

  for (const key in form.fields) {
    const field = form.fields[key]
    if (field.required && !field.value) {
      field.error = 'This field is required'
      isValid = false
    } else {
      field.error = false
    }
  }

  const fields = form.fields
  if ('password' in fields && 'password_confirmation' in fields) {
    if (fields.password.value !== fields.password_confirmation.value) {
      fields.password_confirmation.error = 'Passwords do not match'
      isValid = false
    } else {
      fields.password_confirmation.error = false
    }
  }

  return isValid
}

const showToast = (message, type = 'success') => {
  toast(message, {
    autoClose: 2000,
    type,
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)
}

const submit = async (form: any) => {
  if (!validateForm(form)) return

  try {
    const body = Object.entries(form.fields).reduce(
      (acc, [key, field]: any) => {
        acc[key] = field.value
        return acc
      },
      {}
    )

    const token = await getAccessToken()
    await kftApiRequest(form.url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body,
    })

    showToast(t('general.success'), 'success')
    await fetchProfile()
  } catch (error: any) {
    showToast(t(error.message || 'general.error'), 'error')
    console.error('Submit error:', error)
  }
}

definePageMeta({
  middleware: ['auth'],
  requiresAuth: true,
  isSimpleFooter: true,
})
</script>

<style lang="postcss" scoped>
.content {
  :deep(.account-area__content) {
    box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.06);

    @apply p-6 rounded-lg;
  }
}

.form {
  @apply grid grid-cols-2 gap-4;
}

.input-block {
  @apply col-span-2 md:col-span-1 flex flex-col gap-1 w-full;

  &--full {
    @apply col-span-2;
  }

  &--submit {
    @apply justify-start items-start;
    :deep(button) {
      @apply py-1;
    }
  }
}
</style>
